import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

const CHANNELS = [
  {
    title: "Property portals",
    description:
      "Rightmove, Zoopla, and OnTheMarket are the primary channels for rental property advertising in the UK. Most tenants start their search here, so your listing quality matters enormously. Use professional photos, write detailed descriptions that highlight the lifestyle (not just square footage), and include accurate floorplans. Listings with video stand out significantly in a sea of static images.",
  },
  {
    title: "Social media",
    description:
      "Instagram Reels, TikTok, and Facebook are increasingly where tenants discover rental properties, especially younger renters. Short-form video tours (15-30 seconds) perform exceptionally well, generating shares and saves that extend your reach far beyond your follower count. Post consistently, use local hashtags, and tag the area to reach tenants searching in specific locations.",
  },
  {
    title: "Your agency website",
    description:
      "Your website should showcase available properties with the same quality as portal listings. Ensure rentals are easy to find, filter, and enquire about. Embedding video tours on individual property pages increases time on site and enquiry rates. A well-optimised website also captures tenants who search Google directly rather than using portals.",
  },
  {
    title: "For-let boards",
    description:
      "Physical signage still works, particularly in high-footfall areas. A well-placed for-let board signals availability to local tenants who walk or drive past regularly. Ensure boards are clean, branded, and include a QR code or short URL linking to the full listing with photos and video.",
  },
  {
    title: "Referral networks",
    description:
      "Relocation agents, corporate housing providers, and university accommodation offices are all sources of quality tenants. Build relationships with local employers who regularly relocate staff. A professional marketing pack (including video) makes it easy for referral partners to share your properties with their networks.",
  },
]

const TIPS = [
  {
    title: "Lead with lifestyle, not just specs",
    detail:
      "Tenants are choosing a home, not buying an asset. Highlight the morning coffee on the balcony, the walk to the station, the pub on the corner. Descriptions that paint a picture of daily life generate more enquiries than those listing room dimensions alone.",
  },
  {
    title: "Invest in professional photography",
    detail:
      "Clean, well-lit photos are the minimum. Shoot in natural daylight, declutter every room, and include exterior shots. For higher-value rentals, consider twilight photography. The first image on a portal listing determines whether a tenant clicks through or scrolls past.",
  },
  {
    title: "Create a video tour for every property",
    detail:
      "Video tours help tenants shortlist properties before visiting in person, saving everyone time. A 15-30 second highlight reel works perfectly for social media, while a longer walkthrough suits portal listings. Properties with video receive significantly more enquiries and attract more committed viewers.",
  },
  {
    title: "Price competitively from day one",
    detail:
      "Rental properties that sit on the market lose appeal quickly. Research comparable listings in the area, price realistically, and monitor the market weekly. An empty property earning zero rent is always more expensive than a small concession on the monthly price.",
  },
  {
    title: "Respond to enquiries within the hour",
    detail:
      "Speed wins in the rental market. Tenants often enquire about multiple properties simultaneously and choose the agent who responds first. Set up instant email alerts and aim to respond to every enquiry within 60 minutes during working hours.",
  },
  {
    title: "Screen tenants properly",
    detail:
      "Good marketing attracts more applicants, which means you can be selective. Use thorough referencing (employment verification, previous landlord references, credit checks) to find reliable tenants. A well-marketed property with a strong applicant pool leads to fewer voids and fewer problems.",
  },
]

const FAQ = [
  {
    q: "How do I market a rental property on social media?",
    a: "Create short video tours (15-30 seconds) of the property highlighting its best features and the surrounding area. Post to Instagram Reels, TikTok, and Facebook with local hashtags and the area name. Share across your agency\u2019s social profiles and encourage team members to share from their personal accounts too. Consistency matters more than perfection.",
  },
  {
    q: "Do I need professional photos for a rental listing?",
    a: "Yes. Professional photography is just as important for rentals as it is for sales. Tenants make snap decisions based on listing photos, and dark, cluttered images will cost you enquiries. At a minimum, ensure rooms are well-lit, tidy, and shot from the best angle. Professional photography typically costs \u00a3100-200 and pays for itself in faster lets.",
  },
  {
    q: "How long should a rental property take to let?",
    a: "A well-marketed rental property in a reasonable location should attract strong interest within the first two weeks. If you are not receiving enquiries after 14 days, review your pricing, photography, and listing description. Adding video to the listing often provides an immediate uplift in enquiry rates.",
  },
  {
    q: "Is video marketing worth it for rental properties?",
    a: "Absolutely. Video helps tenants visualise the space and shortlist properties before visiting, which means fewer wasted viewings and more committed applicants. Letting agents who use video report faster lets and better-quality tenants. Tools like QuiteRight make it possible to create professional video from listing photos in under 60 seconds.",
  },
  {
    q: "What portals should I list rental properties on?",
    a: "At minimum, list on Rightmove and Zoopla, which together reach the vast majority of UK tenants. OnTheMarket, SpareRoom (for room lets), and OpenRent are also worth considering depending on your property type. Ensure every portal listing includes professional photos, a floorplan, and ideally a video tour.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function MarketingRentalPropertyGuide({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Market a Rental Property: A Guide for Letting Agents",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Marketing Rental Property" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            How to market a rental property
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            A practical guide for letting agents and landlords. How to
            attract quality tenants faster through better photography,
            video, portal listings, and social media marketing.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Intro */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why rental marketing matters
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Every week a rental property sits empty costs the landlord
              money. On a \u00a31,200/month property, a single void month
              wipes out the equivalent of two months&rsquo; management
              fees. Good marketing fills properties faster, attracts
              better tenants, and reduces costly void periods.
            </p>
            <p>
              Yet rental marketing is often an afterthought. Many letting
              agents invest heavily in sales marketing while treating
              rental listings as second-class citizens: a few phone
              photos, a generic description, and a portal upload. Tenants
              notice the difference, and the best ones choose the
              properties with the best presentation.
            </p>
            <p>
              This guide covers every channel and technique you need to
              market rental properties effectively in 2026.
            </p>
          </div>
        </div>
      </section>

      {/* Marketing channels */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Where to market your rental
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            Effective rental marketing uses multiple channels. Here are
            the five that matter most.
          </p>

          <div className="mt-8 space-y-6">
            {CHANNELS.map((channel, index) => (
              <ScrollReveal key={channel.title}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <div className="flex items-start gap-4">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red/10 font-display text-sm font-bold text-red">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold text-charcoal">
                        {channel.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate">
                        {channel.description}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Six tips for faster lets
            <span className="text-red">.</span>
          </h2>
          <div className="mt-6 space-y-4">
            {TIPS.map((tip) => (
              <div
                key={tip.title}
                className="rounded-xl border border-stone bg-white p-5"
              >
                <h3 className="text-sm font-semibold text-charcoal">
                  {tip.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate">
                  {tip.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Writing a rental listing */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Writing a rental listing that converts
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              A strong rental listing answers three questions before the
              tenant even asks them: what is the property like, what is the
              area like, and what are the terms?
            </p>
            <p>
              Start with a headline that includes the key details: number
              of bedrooms, property type, and location. Follow with a
              short paragraph painting the lifestyle, then list the
              practical details (rent, deposit, available date, furnished
              or unfurnished, parking, pets policy).
            </p>
            <p>
              Avoid generic phrases like &ldquo;must be seen&rdquo; or
              &ldquo;won&rsquo;t last long&rdquo;. Instead, be specific:
              &ldquo;Five-minute walk to Temple Meads station&rdquo; is
              more useful than &ldquo;great transport links&rdquo;.
              Specificity builds trust and pre-qualifies tenants, reducing
              wasted viewings.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "95%", label: "of tenants start their search online" },
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "14 days", label: "target time to let with strong marketing" },
            { stat: "<60s", label: "to create a property video with QuiteRight" },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                href: "/guides/property-video-marketing",
                title: "Property video marketing",
                desc: "The full guide to video marketing for estate and letting agents.",
              },
              {
                href: "/guides/property-photography-tips",
                title: "Property photography tips",
                desc: "How to photograph properties that attract tenants and buyers.",
              },
              {
                href: "/tools/rental-yield-calculator",
                title: "Rental yield calculator",
                desc: "Calculate gross and net rental yields for any property.",
              },
              {
                href: "/guides/property-marketing-checklist",
                title: "Marketing checklist",
                desc: "The complete 24-point property marketing checklist.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Video works for lettings too
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Fill properties faster with scroll-stopping video ads.
            QuiteRight creates professional rental property videos from
            your listing photos in under 60 seconds. Fewer voids, better
            tenants, less hassle.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
